// ===========================================================================
// The conversation with Lumi Staff, client side: the three HTTP calls and what
// their answers mean.
//
// The operator can write to a person as "Lumi Staff", in either age band, and
// the person can answer. It is NOT a regular and has none of a regular's rules:
// no wave, no band to match, and the per-band social and messages switches do
// not apply -- a note from staff is often the very thing explaining why a
// switch has cut somebody off from something. So none of this waits for a
// socket. It is asked over HTTP, person-authenticated exactly as the regulars
// list is (an account bearer and/or the keepsake key, whatever the caller's
// `headers` returns), and it works from the landing and inside a chat alike.
//
// A person can only ever REPLY. The server refuses a first message with 409
// no_thread; this module only reports that. The wire shapes are in
// shared/staff-inbox.js. It touches no DOM, so it is unit-tested with a fake
// fetch. ASCII only.
// ===========================================================================

import { STAFF_PERSON, STAFF_NAME } from '@lumi/shared';

/** The regular-shaped object the thread view opens for this conversation (ui/messages.js, staff mode). */
export const STAFF_ROW = Object.freeze({ personId: STAFF_PERSON, handle: STAFF_NAME, staff: true });

/** A call that has not answered by now is a failure: a hung request must not hold the composer for ever. */
export const STAFF_CALL_MS = 10_000;

/**
 * What a peek, or a read, says about the thread: how many lines it holds, how
 * many of them this person has not seen, and when the last was written. Count 0
 * means staff never wrote.
 * @param {{ count?: number, unread?: number, lastAt?: string }} [body]
 * @returns {{ count: number, unread: number, lastAt: string|null }}
 */
export function staffThreadOf(body) {
  const whole = (n) => Math.max(0, Math.floor(Number(n)) || 0);
  return { count: whole(body?.count), unread: whole(body?.unread), lastAt: typeof body?.lastAt === 'string' ? body.lastAt : null };
}

/** Whether staff has ever written to this person. A null thread is "not known yet" and is no. */
export function hasStaffThread(thread) {
  return !!thread && Number(thread.count) > 0;
}

/** Lines from staff this person has not read. */
export function staffUnread(thread) {
  return thread ? Math.max(0, Number(thread.unread) || 0) : 0;
}

/** Whether two answers about the thread say the same, so a redraw can be skipped. */
export function sameStaffThread(a, b) {
  if (!a || !b) return !a && !b;
  return a.count === b.count && a.unread === b.unread && a.lastAt === b.lastAt;
}

/** A line the thread view can draw: it has words. */
function isLine(m) {
  return !!m && typeof m === 'object' && typeof m.text === 'string';
}

/**
 * Oldest first, whatever order they arrived in. If any line has no usable time
 * the order cannot be judged, so the server's own order stands.
 */
export function oldestFirst(messages) {
  const stamped = messages.map((m, i) => ({ m, i, ms: Date.parse(m.at || '') }));
  if (stamped.some((x) => !Number.isFinite(x.ms))) return messages.slice();
  return stamped.sort((a, b) => a.ms - b.ms || a.i - b.i).map((x) => x.m);
}

/** The server's word for what went wrong, else one from the status. */
function errorOf(r) {
  if (typeof r.body.error === 'string' && r.body.error) return r.body.error;
  if (r.status === 429) return 'rate_limited';
  if (r.status === 409) return 'no_thread';
  return 'failed';
}

/**
 * @param {object} o
 * @param {string} o.base  the server's HTTP origin
 * @param {() => Record<string, string>} o.headers  person auth; empty means nobody to ask about
 * @param {typeof fetch} [o.fetchImpl]
 * @param {number} [o.timeoutMs]
 */
export function createStaffInboxClient({ base, headers, fetchImpl = (...a) => globalThis.fetch(...a), timeoutMs = STAFF_CALL_MS }) {
  /** One call. Null when there is nobody to ask about; else { ok, status, body }, a failure included. */
  async function call(query, init = {}) {
    const auth = headers();
    if (!Object.keys(auth).length) return null;
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), timeoutMs) : null;
    try {
      const res = await fetchImpl(`${base}/api/staff-inbox${query}`, {
        ...init,
        headers: { ...auth, ...(init.headers || {}) },
        ...(ctl ? { signal: ctl.signal } : {}),
      });
      const body = await res.json().catch(() => ({}));
      return { ok: !!res.ok, status: res.status, body: body && typeof body === 'object' ? body : {} };
    } catch {
      return { ok: false, status: 0, body: {} };
    } finally {
      clearTimeout(timer);
    }
  }

  return {
    /**
     * How the thread stands. Marks nothing seen. `nobody` is a person with no
     * identity to ask about, who has no thread by definition.
     * @returns {Promise<{ ok: true, nobody?: true, thread: object|null } | { ok: false, error: string }>}
     */
    async peek() {
      const r = await call('?peek=1');
      if (!r) return { ok: true, nobody: true, thread: null };
      if (!r.ok) return { ok: false, error: errorOf(r) };
      return { ok: true, thread: staffThreadOf(r.body) };
    },

    /**
     * Every line, oldest first. The server marks them seen, so `thread.unread`
     * comes back 0.
     * @returns {Promise<{ ok: true, thread: object, messages: object[] } | { ok: false, error: string }>}
     */
    async read() {
      const r = await call('');
      if (!r) return { ok: false, error: 'failed' };
      if (!r.ok) return { ok: false, error: errorOf(r) };
      return { ok: true, thread: staffThreadOf(r.body), messages: oldestFirst(Array.isArray(r.body.messages) ? r.body.messages.filter(isLine) : []) };
    },

    /**
     * Put one line away, once it has been read: it leaves this person's view
     * and counts, and stays in the console's. `error` is the server's code --
     * not_read, not_found -- or 'failed'.
     * @returns {Promise<{ ok: true, id: string } | { ok: false, error: string }>}
     */
    async hide(id) {
      const r = await call('/hide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      if (!r) return { ok: false, error: 'failed' };
      if (!r.ok) return { ok: false, error: errorOf(r) };
      return { ok: true, id };
    },

    /**
     * A reply. `error` is the server's code -- no_thread, bad_message,
     * rate_limited -- or 'failed' when nothing usable came back.
     * @returns {Promise<{ ok: true, message: object } | { ok: false, error: string }>}
     */
    async send(text, id) {
      const r = await call('', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(id ? { text, id } : { text }),
      });
      if (!r) return { ok: false, error: 'failed' };
      if (!r.ok || !isLine(r.body.message)) return { ok: false, error: errorOf(r) };
      return { ok: true, message: r.body.message };
    },
  };
}

export default { createStaffInboxClient, staffThreadOf, hasStaffThread, staffUnread, sameStaffThread, oldestFirst, STAFF_ROW, STAFF_CALL_MS };
